import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { LearningResourceViewModel } from '@app/inputs/models/learning-resource.view-model';
import {
  SearchSubmitMethod,
  SearchTrackerService,
} from '@app/search/services/search-tracker.service';
import { MenuViewModel } from '@app/shared/components/menu/menu.component';
import { NUM_RESULTS_PER_PAGE } from '@app/shared/search.token';
import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

@Component({
  selector: 'dgx-search-learnings',
  templateUrl: './search-learnings.component.html',
  styleUrls: ['./search-learnings.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SearchLearningsComponent implements OnInit {
  public menuConfig: MenuViewModel[];
  public i18n = this.translateService.instant([
    'Core_SortBy',
    'Core_MoreOptions',
    'LearningSearch_MostPopular',
    'LearningSearch_MostRecent',
    'LearningSearch_MostRelevant',
    'LearningSearch_Popular',
    'LearningSearch_Recent',
    'LearningSearch_Relevant',
  ]);

  @Output() public pageChange = new EventEmitter<number>();
  @Output() public sortChange = new EventEmitter<string>();

  @Input() public results: LearningResourceViewModel[];
  @Input() public isLoading: boolean;
  @Input() public totalContentResults: number;
  @Input() public searchTerm: string;
  @Input() public pageNum: number = 1;

  constructor(
    private translateService: TranslateService,
    private searchTrackerService: SearchTrackerService,
    private route: ActivatedRoute,
    @Inject(NUM_RESULTS_PER_PAGE) public readonly numResultsPerPage: number
  ) {}

  public get sortButtonText(): Observable<string> {
    return this.route.queryParams.pipe(
      map(({ sort }) => {
        switch (sort) {
          case 'popular':
            return this.i18n.LearningSearch_MostPopular;
          case 'recent':
            return this.i18n.LearningSearch_MostRecent;
          default:
            return this.i18n.LearningSearch_MostRelevant;
        }
      })
    );
  }

  public get showSort(): boolean {
    return this.totalContentResults > 0;
  }

  public get showNoResults(): boolean {
    return this.totalContentResults === 0;
  }

  public get showPagination(): boolean {
    return this.totalContentResults > this.numResultsPerPage;
  }

  public get resultsRange(): string {
    return this.translateService.instant('LearningSearch_ResultSetRangeCount', {
      rangeStart: this.rangeStart,
      rangeEnd: this.rangeEnd,
      totalNumResults: this.totalContentResults,
    });
  }

  private get rangeStart(): number {
    return this.limit - this.numResultsPerPage + 1;
  }

  private get rangeEnd(): number {
    return this.limit > this.totalContentResults
      ? this.totalContentResults
      : this.limit;
  }

  private get limit(): number {
    return this.pageNum * this.numResultsPerPage;
  }

  public ngOnInit(): void {
    this.menuConfig = [
      {
        title: this.i18n.LearningSearch_Popular,
        defaultAction: () => this.doSortChange(SearchSubmitMethod.popular),
      },
      {
        title: this.i18n.LearningSearch_Recent,
        defaultAction: () => this.doSortChange(SearchSubmitMethod.recent),
      },
      {
        title: this.i18n.LearningSearch_Relevant,
        defaultAction: () => this.doSortChange(SearchSubmitMethod.relevant),
      },
    ];
  }

  public onInputSelected(resource: LearningResourceViewModel, index: number) {
    const resultPosition =
      this.pageNum * this.numResultsPerPage -
      (this.numResultsPerPage - index) +
      1;
    this.searchTrackerService.contentClicked(resource, {
      location: 'Search',
      searchTerm: this.searchTerm,
      currentPage: this.pageNum,
      resultPosition,
      totalContentResults: this.totalContentResults,
    });
  }

  public trackByFn(index: number, item: LearningResourceViewModel): string {
    return item.trackingKey;
  }

  private doSortChange(sortType: SearchSubmitMethod): void {
    this.sortChange.emit(sortType.toLowerCase());
  }
}
